import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DefaultLanguageState } from 'app/states/default-language/default-language.state';
import { Observable, Subscription } from 'rxjs';
import { Select, Store } from '@ngxs/store';

@Component({
    selector: 'mh-case-dismiss-modal',
    templateUrl: './case-discard-modal.component.html',
    styleUrls: ['./case-discard-modal.component.scss'],
    standalone: false
})
export class CaseDiscardModalComponent implements OnInit, OnDestroy {
  @Select(DefaultLanguageState.languageId) languageId$: Observable<number>;
  @Input() reasons;
  @Input() title = 'cases.detail_modal.enter_a_reason';
  @Input() showButtons = true;
  @Input() OtherOptionAvailable = true;
  @Input() customClass: string = '';
  @Input() initialValue: string | number = '';
  @Input() tooltipHighlight: string = '';
  @Output() handleClose = new EventEmitter();
  @Output() handleDiscardCase = new EventEmitter();
  @Output() reasonChange = new EventEmitter<string | number>();
  @Output() childsChange = new EventEmitter<{ reasonId: string | number; childs: { [key: string]: boolean } }>();

  reasonForm;
  langId;
  subscription = new Subscription();
  private isInitialValue = true;

  constructor(private fb: FormBuilder, private store: Store) {}

  ngOnInit(): void {
    const formControls: any = {
      reason: [this.initialValue, []],
      reasonComment: ['', []],
    };

    this.langId = this.store.selectSnapshot(DefaultLanguageState.languageId);
    
    if (this.reasons && Array.isArray(this.reasons)) {
      this.reasons = [...this.reasons].map((reason) => {
        const processedReason = {
          ...reason,
          reason: this.displayFn(reason),
          disabled: reason.disabled || false,
          tooltip: reason.tooltip,
          subtitleParams: reason.subtitleParams,
          childs: reason.childs || [],
        };

        if (reason.childs && Array.isArray(reason.childs)) {
          reason.childs.forEach((child: any) => {
            const controlName = `child_${reason.id}_${child.id}`;
            formControls[controlName] = [false, []];
          });
        }

        return processedReason;
      });
    }

    this.reasonForm = this.fb.group(formControls);

    const reasonControl = this.reasonForm.get('reason');
    if (reasonControl) {
      this.subscription.add(
        reasonControl.valueChanges.subscribe((value) => {
          if (this.isInitialValue) {
            this.isInitialValue = false;
            return;
          }
          this.reasonChange.emit(value);
        })
      );
      
      setTimeout(() => {
        this.isInitialValue = false;
      }, 100);
    }

    this.subscription.add(
      this.languageId$.subscribe((langId) => {
        this.langId = langId;
        if (this.reasons && Array.isArray(this.reasons)) {
          this.reasons = [...this.reasons].map((reason) => {
            return {
              ...reason,
              reason: this.displayFn(reason),
              disabled: reason.disabled || false,
              tooltip: reason.tooltip,
              subtitleParams: reason.subtitleParams,
              childs: reason.childs || [],
            };
          });
        }
      }),
    );
  }

  displayFn(option) {
    return option.translates?.find((translate) => translate.language_id === this.langId)?.name ?? option.reason;
  }

  discardCase() {
    this.handleDiscardCase.next(this.reasonForm.value);
    this.handleClose.next(true);
  }

  cancelDiscard() {
    this.handleClose.next(true);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get currentOption(): string | number {
    return this.reasonForm.get('reason').value;
  }

  onInputClick(reasonId: string | number) {
    const reasonControl = this.reasonForm.get('reason');
    if (!reasonControl?.disabled) {
      const currentValue = reasonControl.value;
      if (currentValue !== reasonId) {
        this.isInitialValue = false;
        reasonControl.setValue(reasonId, { emitEvent: true });
      } else {
        this.isInitialValue = false;
        this.reasonChange.emit(reasonId);
      }
    }
  }

  onChildChange(reasonId: string | number, childId: string, checked: boolean) {
    const controlName = `child_${reasonId}_${childId}`;
    const control = this.reasonForm.get(controlName);
    if (control) {
      control.setValue(checked, { emitEvent: false });
    }

    const reason = this.reasons?.find((r: any) => r.id === reasonId);
    if (reason && reason.childs) {
      const childsValues: { [key: string]: boolean } = {};
      reason.childs.forEach((child: any) => {
        const childControlName = `child_${reasonId}_${child.id}`;
        const childControl = this.reasonForm.get(childControlName);
        childsValues[child.id] = childControl?.value || false;
      });
      this.childsChange.emit({ reasonId, childs: childsValues });
    }
  }

  getChildControlName(reasonId: string | number, childId: string): string {
    return `child_${reasonId}_${childId}`;
  }

  getChildValue(reasonId: string | number, childId: string): boolean {
    const controlName = this.getChildControlName(reasonId, childId);
    const control = this.reasonForm.get(controlName);
    return control?.value || false;
  }
}
